import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { NewOfferteDialog } from '../components/offertes/NewOfferteDialog';
import {
  fetchOfferteList,
  searchOffertes,
  type OfferteListItem,
  type OfferteListView,
} from '../lib/offertes';
import { leadStatusBadge } from '../lib/offerte-status';
import { formatCurrency, formatRelativeTime } from '../lib/format';

export function OffertesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<OfferteListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<OfferteListItem[] | null>(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [view, setView] = useState<OfferteListView>('open');

  // Initial load + reload bij view-wissel (Open ↔ Wacht op klant)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchOfferteList(view)
      .then((list) => {
        if (!cancelled) {
          setItems(list);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message || 'Kon offertes niet laden');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  // Debounced server-side search when query >= 2 chars
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(() => {
      searchOffertes(trimmed, controller.signal)
        .then((results) => {
          setSearchResults(results);
          setSearching(false);
        })
        .catch((err: Error & { name?: string }) => {
          if (err.name === 'AbortError') return;
          setSearching(false);
          setSearchResults([]);
        });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const isSearching = query.trim().length >= 2;
  const displayItems = isSearching ? (searchResults ?? []) : items;

  return (
    <>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Offertes</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {isSearching
                  ? 'Zoekt in álle Kleda-offertes — elke status, geen datumgrens'
                  : view === 'wacht'
                    ? 'Offertes waar de klant op terugkomt'
                    : 'Open Kleda-offertes van de laatste 30 dagen'}
              </p>
              <div className="flex items-center gap-1.5 mt-2">
                {([
                  { key: 'open', label: 'Open' },
                  { key: 'wacht', label: 'Wacht op klant' },
                ] as const).map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setView(t.key)}
                    className={clsx(
                      'text-xs px-2.5 py-1 rounded-full font-medium transition-colors',
                      view === t.key
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-1 justify-end max-w-2xl">
              <div className="relative flex-1 max-w-sm">
                <svg
                  className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Zoek naam, offertenr, email, postcode..."
                  className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-3.5 h-3.5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setNewDialogOpen(true)}
                className="text-sm px-3 py-2 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 whitespace-nowrap"
              >
                + Nieuwe offerte
              </button>
            </div>
          </div>
        </div>

        <NewOfferteDialog
          isOpen={newDialogOpen}
          onClose={() => setNewDialogOpen(false)}
          onCreated={(leadId) => {
            setNewDialogOpen(false);
            navigate(`/offertes/${leadId}`);
          }}
        />

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 space-y-2 animate-pulse">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            </div>
          ) : !isSearching && items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p className="text-sm">
                {view === 'wacht'
                  ? 'Geen offertes die op de klant wachten'
                  : 'Geen open offertes in de laatste 30 dagen'}
              </p>
            </div>
          ) : isSearching && !searching && displayItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 gap-2">
              <p className="text-sm">Geen offertes gevonden voor "{query}"</p>
              <button
                onClick={() => setQuery('')}
                className="text-xs text-indigo-600 hover:underline"
              >
                Zoekopdracht wissen
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-50 sticky top-0">
                <tr>
                  <th className="text-left font-medium px-6 py-2">Leadnr</th>
                  <th className="text-left font-medium px-4 py-2">Naam</th>
                  <th className="text-left font-medium px-4 py-2">Plaats</th>
                  <th className="text-right font-medium px-4 py-2">Totaal</th>
                  <th className="text-left font-medium px-4 py-2">Status</th>
                  <th className="text-left font-medium px-4 py-2">Leeftijd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {displayItems.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => navigate(`/offertes/${row.id}`)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-6 py-3 font-medium text-slate-900">
                      {row.leadnummer || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{row.name}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {[row.postcode, row.city].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-900">
                      {row.totalBasis > 0 ? formatCurrency(row.totalBasis) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const badge = leadStatusBadge(row.status);
                        return (
                          <span
                            className={clsx(
                              'text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap',
                              badge.color
                            )}
                          >
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {formatRelativeTime(row.createdDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
